import { useEffect, useMemo, useRef } from "react"
import { createQueryPersister } from "../../contexts/ServerContext/createQueryPersister"
import type {
  UseScopedQueryPersistenceOptions,
  UseScopedQueryPersistenceResult,
} from "./useScopedQueryPersistence.types"

export const useScopedQueryPersistence = ({
  cacheBusterVersion,
  isLoading,
  keyPrefix,
  storage,
  userSub,
}: UseScopedQueryPersistenceOptions): UseScopedQueryPersistenceResult => {
  const scopedKey = !isLoading && userSub ? `${keyPrefix}:${userSub}` : null
  const previousScopedKeyRef = useRef<string | null>(null)

  const persister = useMemo(
    () =>
      scopedKey && storage
        ? createQueryPersister({
            storage,
            key: scopedKey,
          })
        : undefined,
    [scopedKey, storage]
  )

  useEffect(() => {
    if (!storage) {
      return
    }

    Promise.resolve(storage.removeItem(keyPrefix)).catch(() => undefined)
  }, [keyPrefix, storage])

  useEffect(() => {
    if (!storage) {
      previousScopedKeyRef.current = scopedKey
      return
    }

    const previousScopedKey = previousScopedKeyRef.current
    previousScopedKeyRef.current = scopedKey

    if (previousScopedKey && previousScopedKey !== scopedKey) {
      Promise.resolve(storage.removeItem(previousScopedKey)).catch(
        () => undefined
      )
    }
  }, [scopedKey, storage])

  return {
    cacheBusterVersion: scopedKey
      ? `${cacheBusterVersion ?? "dev"}:${userSub}`
      : cacheBusterVersion,
    persister,
    providerKey:
      scopedKey ?? `${keyPrefix}:${isLoading ? "loading" : "anonymous"}`,
  }
}
